import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import WOW from "wow.js";

import HeaderOne from "../components/header/HeaderOne";
import Breadcrumb from "./Breadcrumb";
import FooterOne from "../components/footer/FooterOne";

function DynamicModulePage() {
  const location = useLocation();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const API_BASE_URL = (
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
  ).replace(/\/+$/, "");

  /*
  |--------------------------------------------------------------------------
  | DİL
  |--------------------------------------------------------------------------
  */

  const searchParams = new URLSearchParams(location.search);

  const currentLang = searchParams.get("lang") || "tr";

  const isContractPage = location.pathname.startsWith("/sozlesmeler/");

  const contractId = isContractPage
    ? location.pathname.match(/(\d+)\/?$/)?.[1]
    : null;

  /*
  |--------------------------------------------------------------------------
  | GÖRSEL URL
  |--------------------------------------------------------------------------
  */

  const getImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== "string") {
      return "";
    }

    const path = imagePath.trim();

    if (!path) {
      return "";
    }

    if (
      path.startsWith("http://") ||
      path.startsWith("https://") ||
      path.startsWith("data:") ||
      path.startsWith("blob:")
    ) {
      return path;
    }

    if (path.startsWith("/cdn/")) {
      return `${API_BASE_URL}${path}`;
    }

    if (path.startsWith("cdn/")) {
      return `${API_BASE_URL}/${path}`;
    }

    const cleanPath = path.replace(/^\/+/, "");

    return `${API_BASE_URL}/cdn/${cleanPath}`;
  };

  /*
  |--------------------------------------------------------------------------
  | SAYFAYI ÇEK
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    const controller = new AbortController();

    const fetchDynamicPage = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        setPageData(null);

        let response;

        /*
      |--------------------------------------------------------------------------
      | SÖZLEŞME SAYFASI
      |--------------------------------------------------------------------------
      */
        if (isContractPage) {
          if (!contractId) {
            setNotFound(true);
            return;
          }

          response = await fetch(`${API_BASE_URL}/api/contract/${contractId}`, {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          });
        } else {

        /*
      |--------------------------------------------------------------------------
      | NORMAL DİNAMİK SAYFA
      |--------------------------------------------------------------------------
      */
          const pagePath = location.pathname;

          response = await fetch(
            `${API_BASE_URL}/api/frontend-menu/resolve-page?path=${encodeURIComponent(
              pagePath,
            )}&lang=${encodeURIComponent(currentLang)}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          );
        }

        if (response.status === 404) {
          setNotFound(true);
          setPageData(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Dinamik sayfa alınamadı: ${response.status}`);
        }

        const data = await response.json();

        if (!data) {
          setNotFound(true);
          setPageData(null);
          return;
        }

        /*
      |--------------------------------------------------------------------------
      | SÖZLEŞME VERİSİNİ DİNAMİK SAYFA FORMATINA ÇEVİR
      |--------------------------------------------------------------------------
      */
        if (isContractPage) {
          setPageData({
            id: data.id,
            title: data.title || "Sözleşme",

            description: data.shortDescription || "",

            editor: data.editor || "",

            image: "",
            coverImg: "",
          });

          return;
        }

        /*
      |--------------------------------------------------------------------------
      | NORMAL DİNAMİK SAYFA
      |--------------------------------------------------------------------------
      */
        setPageData(data);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("DYNAMIC_PAGE_API_ERROR:", error);

          setNotFound(true);
          setPageData(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchDynamicPage();

    return () => {
      controller.abort();
    };
  }, [
    API_BASE_URL,
    location.pathname,
    currentLang,
    isContractPage,
    contractId,
  ]);

  /*
  |--------------------------------------------------------------------------
  | WOW
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    if (!pageData) {
      return;
    }

    const wow = new WOW({
      boxClass: "wow",
      animateClass: "animated",
      offset: 0,
      mobile: true,
      live: true,
    });

    wow.init();
  }, [pageData]);

  /*
  |--------------------------------------------------------------------------
  | VERİLER
  |--------------------------------------------------------------------------
  */

  const title = String(pageData?.title || "").trim();

  const description = String(pageData?.description || "").trim();

  const editor = String(pageData?.editor || "").trim();

  /*
   * Önce image.
   * Yoksa coverImg.
   */
  const imagePath = pageData?.image || pageData?.coverImg || "";

  const pageImage = getImageUrl(imagePath);

  const hasImage = Boolean(pageImage);

  const hasDescription = Boolean(description);

  const hasEditor = Boolean(editor);

  /*
  |--------------------------------------------------------------------------
  | BREADCRUMB
  |--------------------------------------------------------------------------
  */

  const breadcrumbs = [
    {
      label: "Ana Sayfa",
      link:
        currentLang === "tr"
          ? "/"
          : `/?lang=${encodeURIComponent(currentLang)}`,
    },
    {
      label: isContractPage ? "Sözleşmeler" : "Kurumsal",
    },
  ];

  /*
   * Hakkımızda, Vizyonumuz vs.
   * breadcrumb'ın sonuna ekle.
   */
  if (title) {
    breadcrumbs.push({
      label: title,
    });
  }

  /*
  |--------------------------------------------------------------------------
  | İÇERİĞİ RENDER ET
  |--------------------------------------------------------------------------
  */

  const renderContent = () => {
    return (
      <div className="dynamic-page-content">
        {title && (
          <div className="dynamic-page-heading">
            <h1>{title}</h1>

            <span className="dynamic-page-heading-line" />
          </div>
        )}

        {/*
         * DESCRIPTION VARSA BAS
         */}
        {hasDescription && (
          <div className="dynamic-page-description">{description}</div>
        )}

        {/*
         * EDITOR VARSA HTML OLARAK BAS
         */}
        {hasEditor && (
          <div
            className="dynamic-page-editor"
            dangerouslySetInnerHTML={{
              __html: editor,
            }}
          />
        )}

        {/*
         * HİÇ İÇERİK YOKSA
         */}
        {!hasDescription && !hasEditor && (
          <div className="dynamic-page-empty">
            Bu sayfa için henüz içerik eklenmemiş.
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dynamic-page-layout">
      <style>
        {`
          /*
          |--------------------------------------------------------------------------
          | TÜM SAYFA
          |--------------------------------------------------------------------------
          |
          | Header yukarıda,
          | footer aşağıda.
          |
          | İçerik azsa sayfa yine ekranı doldurur.
          | İçerik çoksa doğal şekilde aşağı uzar.
          */

          .dynamic-page-layout {
            display: flex;
            flex-direction: column;

            width: 100%;
            min-height: 100vh;
          }

          .dynamic-page-main {
            display: flex;
            flex: 1 0 auto;
            flex-direction: column;

            width: 100%;
          }

          /*
          |--------------------------------------------------------------------------
          | İÇERİK ALANI
          |--------------------------------------------------------------------------
          */

.demirsan-dynamic-page {
  flex: 1 0 auto;
  width: 100%;

  padding-top: 30px;
  padding-bottom: 30px;
}

          /*
          |--------------------------------------------------------------------------
          | BAŞLIK
          |--------------------------------------------------------------------------
          */

          .dynamic-page-heading {
            position: relative;

            margin-bottom: 30px;
            padding-bottom: 18px;
          }

          .dynamic-page-heading h1 {
            margin: 0;
            padding: 0;

            color: #1c2539;

            font-family: inherit;
            font-size: 42px;
            font-weight: 700;
            line-height: 1.2;
          }

          .dynamic-page-heading-line {
            position: absolute;

            left: 0;
            bottom: 0;

            display: block;

            width: 55px;
            height: 3px;

            background: #df0a0a;
          }

          /*
          |--------------------------------------------------------------------------
          | DESCRIPTION
          |--------------------------------------------------------------------------
          */

          .dynamic-page-description {
            width: 100%;

            margin-bottom: 22px;

            color: #333333;

            font-size: 17px;
            font-weight: 400;
            line-height: 1.9;

            text-align: justify;
          }

          /*
          |--------------------------------------------------------------------------
          | EDITOR
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor {
            width: 100%;

            color: #333333;

            font-family: inherit;
            font-size: 17px;
            font-weight: 400;
            line-height: 1.9;

            overflow-wrap: break-word;
          }

          /*
           * İlk ve son element gereksiz boşluk bırakmasın.
           */
          .dynamic-page-editor
            > *:first-child {
            margin-top: 0 !important;
          }

          .dynamic-page-editor
            > *:last-child {
            margin-bottom: 0 !important;
          }

          /*
          |--------------------------------------------------------------------------
          | EDITOR PARAGRAF
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor p {
            margin-top: 0;
            margin-bottom: 18px;

            color: #333333;

            font-family: inherit;
            font-size: 17px;
            font-weight: 400;
            line-height: 1.9;

            text-align: justify;
          }

          /*
           * Word'den gelen MsoNormal vs.
           * site fontunu kullanmaya devam etsin.
           */
          .dynamic-page-editor div,
          .dynamic-page-editor span,
          .dynamic-page-editor li,
          .dynamic-page-editor td,
          .dynamic-page-editor th {
            font-family: inherit;
          }

          /*
          |--------------------------------------------------------------------------
          | EDITOR BAŞLIKLARI
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor h1,
          .dynamic-page-editor h2,
          .dynamic-page-editor h3,
          .dynamic-page-editor h4,
          .dynamic-page-editor h5,
          .dynamic-page-editor h6 {
            color: #1c2539;

            font-family: inherit;
            font-weight: 700;

            line-height: 1.35;
          }

          .dynamic-page-editor h1 {
            margin: 32px 0 18px;

            font-size: 36px;
          }

          .dynamic-page-editor h2 {
            margin: 30px 0 16px;

            font-size: 30px;
          }

          .dynamic-page-editor h3 {
            margin: 26px 0 14px;

            font-size: 24px;
          }

          .dynamic-page-editor h4 {
            margin: 24px 0 12px;

            font-size: 20px;
          }

          /*
          |--------------------------------------------------------------------------
          | EDITOR LISTELER
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor ul,
          .dynamic-page-editor ol {
            margin: 0 0 22px;

            padding-left: 26px;
          }

          .dynamic-page-editor li {
            margin-bottom: 7px;

            color: #333333;

            font-size: 17px;
            line-height: 1.8;
          }

          /*
          |--------------------------------------------------------------------------
          | EDITOR IMAGE
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor img {
            display: block;

            max-width: 100%;
            height: auto;

            margin-top: 25px;
            margin-bottom: 25px;

            border-radius: 8px;
          }

          /*
          |--------------------------------------------------------------------------
          | EDITOR TABLE
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor table {
            width: 100% !important;
            max-width: 100% !important;

            margin: 25px 0;

            border-collapse: collapse;
          }

          .dynamic-page-editor th,
          .dynamic-page-editor td {
            padding: 12px 14px;

            border:
              1px solid
              rgba(0, 0, 0, 0.12);
          }

          .dynamic-page-editor th {
            color: #1c2539;
            background: #f5f6f8;

            font-weight: 700;
          }

          /*
          |--------------------------------------------------------------------------
          | LINKLER
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor a {
            color: #df0a0a;

            text-decoration: underline;

            text-underline-offset: 3px;
          }

          /*
          |--------------------------------------------------------------------------
          | BLOCKQUOTE
          |--------------------------------------------------------------------------
          */

          .dynamic-page-editor blockquote {
            margin: 25px 0;

            padding: 18px 22px;

            color: #353535;
            background: #f6f7f9;

            border-left:
              4px solid #df0a0a;
          }

          /*
          |--------------------------------------------------------------------------
          | SAĞ IMAGE
          |--------------------------------------------------------------------------
          */

          .dynamic-page-image {
            width: 100%;

            overflow: hidden;

            background: #f4f4f4;

            border-radius: 10px;

            box-shadow:
              0 10px 30px
              rgba(0, 0, 0, 0.10);
          }

          .dynamic-page-image img {
            display: block;

            width: 100%;
            height: 430px;

            object-fit: cover;
            object-position: center;
          }

          /*
          |--------------------------------------------------------------------------
          | IMAGE YOKSA
          |--------------------------------------------------------------------------
          |
          | İçeriği col-6'ya sıkıştırma.
          | Container'ın tamamını kullansın.
          */

          .dynamic-page-full-content {
            width: 100%;
            max-width: 100%;
          }

          .dynamic-page-full-content
            .dynamic-page-content,
          .dynamic-page-full-content
            .dynamic-page-description,
          .dynamic-page-full-content
            .dynamic-page-editor {
            width: 100%;
            max-width: 100%;
          }

          /*
          |--------------------------------------------------------------------------
          | BOŞ İÇERİK
          |--------------------------------------------------------------------------
          */

          .dynamic-page-empty {
            width: 100%;

            padding: 20px;

            color: #6c7280;
            background: #f6f7f9;

            border-radius: 7px;

            font-size: 15px;
          }

          /*
          |--------------------------------------------------------------------------
          | LOADING
          |--------------------------------------------------------------------------
          */

          .dynamic-page-loading {
            width: 100%;

            padding: 35px 0;

            color: #6c7280;

            font-size: 16px;
          }

          /*
          |--------------------------------------------------------------------------
          | 404
          |--------------------------------------------------------------------------
          */

          .dynamic-page-not-found {
            width: 100%;

            padding: 40px 0;
          }

          .dynamic-page-not-found h2 {
            margin: 0 0 10px;

            color: #1c2539;

            font-size: 32px;
          }

          /*
          |--------------------------------------------------------------------------
          | TABLET
          |--------------------------------------------------------------------------
          */

          @media (max-width: 991px) {

            .demirsan-dynamic-page {
              padding-top: 30px;
              padding-bottom: 30px;
            }

            .dynamic-page-image {
              margin-bottom: 35px;
            }

            .dynamic-page-image img {
              height: auto;
              min-height: 320px;
              max-height: 500px;
            }

            .dynamic-page-heading h1 {
              font-size: 36px;
            }

            .dynamic-page-description,
            .dynamic-page-editor p {
              text-align: left;
            }
          }

          /*
          |--------------------------------------------------------------------------
          | TELEFON
          |--------------------------------------------------------------------------
          */

          @media (max-width: 767px) {

            .demirsan-dynamic-page {
              padding-top: 25px;
              padding-bottom: 25px;
            }

            .dynamic-page-heading {
              margin-bottom: 22px;

              padding-bottom: 14px;
            }

            .dynamic-page-heading h1 {
              font-size: 30px;
              line-height: 1.25;
            }

            .dynamic-page-heading-line {
              width: 45px;
            }

            .dynamic-page-description,
            .dynamic-page-editor,
            .dynamic-page-editor p,
            .dynamic-page-editor li {
              font-size: 15px;
              line-height: 1.75;
            }

            .dynamic-page-editor h1 {
              font-size: 29px;
            }

            .dynamic-page-editor h2 {
              font-size: 25px;
            }

            .dynamic-page-editor h3 {
              font-size: 21px;
            }

            .dynamic-page-image img {
              min-height: 240px;
            }

            .dynamic-page-editor table {
              display: block;

              max-width: 100%;

              overflow-x: auto;

              white-space: nowrap;
            }
          }
            /*
|--------------------------------------------------------------------------
| EDITORDEN GELEN LİSTELER
|--------------------------------------------------------------------------
*/

.dynamic-page-editor ul {
  display: block !important;

  margin: 0 0 22px !important;
  padding-left: 32px !important;

  list-style-type: disc !important;
  list-style-position: outside !important;
}

.dynamic-page-editor ol {
  display: block !important;

  margin: 0 0 22px !important;
  padding-left: 32px !important;

  list-style-type: decimal !important;
  list-style-position: outside !important;
}

/*
 * Tema li'leri list-style:none veya display:block
 * yapıyorsa geri aç.
 */
.dynamic-page-editor ul > li,
.dynamic-page-editor ol > li {
  display: list-item !important;

  margin: 0 0 10px !important;
  padding-left: 4px !important;

  color: #333333;

  font-size: 17px;
  font-weight: 500;
  line-height: 1.8;
}

.dynamic-page-editor ul > li {
  list-style-type: disc !important;
}

.dynamic-page-editor ol > li {
  list-style-type: decimal !important;
}

/*
 * Bullet / numara görünümü
 */
.dynamic-page-editor li::marker {
  color: #1c2539 !important;
  font-size: 1.05em;
  font-weight: 700;
}

/*
 * Senin HTML'inde li içinde p var.
 * Gereksiz paragraf boşluğu oluşturmasın.
 */
.dynamic-page-editor li > p {
  display: inline !important;

  margin: 0 !important;
  padding: 0 !important;

  line-height: inherit !important;
}

/*
 * İç içe liste gelirse
 */
.dynamic-page-editor ul ul {
  margin-top: 8px !important;
  margin-bottom: 8px !important;

  list-style-type: circle !important;
}

.dynamic-page-editor ul ul > li {
  list-style-type: circle !important;
}

.dynamic-page-editor ul ul ul {
  list-style-type: square !important;
}

.dynamic-page-editor ul ul ul > li {
  list-style-type: square !important;
}
        `}
      </style>

      {/* =====================================================
          HEADER
      ====================================================== */}
      <HeaderOne />

      {/* =====================================================
          BREADCRUMB
      ====================================================== */}
      <Breadcrumb
        title={title || (loading ? "Kurumsal" : "Sayfa")}
        breadcrumbs={breadcrumbs}
      />

      {/* =====================================================
          HEADER İLE FOOTER ARASINDAKİ ANA ALAN
      ====================================================== */}
      <main className="dynamic-page-main">
        {/*
        =====================================================
        LOADING
        =====================================================
        */}
        {loading && (
          <section className="demirsan-dynamic-page">
            <div className="container">
              <div className="dynamic-page-loading">İçerik yükleniyor...</div>
            </div>
          </section>
        )}

        {/*
        =====================================================
        NOT FOUND
        =====================================================
        */}
        {!loading && (notFound || !pageData) && (
          <section className="demirsan-dynamic-page">
            <div className="container">
              <div className="dynamic-page-not-found">
                <h2>Sayfa bulunamadı.</h2>

                <p>Aradığınız içerik mevcut değil.</p>
              </div>
            </div>
          </section>
        )}

        {/*
        =====================================================
        SAYFA BULUNDU
        =====================================================
        */}
        {!loading && !notFound && pageData && (
          <section className="demirsan-dynamic-page">
            <div className="container">
              {/*
                =============================================
                IMAGE VAR
                =============================================
                */}
              {hasImage ? (
                <div className="row g-5 align-items-center">
                  {/* SOL İÇERİK */}
                  <div className="col-lg-6 col-md-12 order-lg-1 order-2">
                    {renderContent()}
                  </div>

                  {/* SAĞ IMAGE */}
                  <div className="col-lg-6 col-md-12 order-lg-2 order-1">
                    <div
                      className="dynamic-page-image wow fadeInRight"
                      data-wow-delay=".2s"
                    >
                      <img
                        src={pageImage}
                        alt={title || "Demirsan"}
                        loading="lazy"
                        onError={(event) => {
                          console.error(
                            "DYNAMIC_PAGE_IMAGE_ERROR:",
                            event.currentTarget.src,
                          );

                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /*
                  ===========================================
                  IMAGE YOK
                  ===========================================
                  */

                <div className="row">
                  <div className="col-12">
                    <div className="dynamic-page-full-content">
                      {renderContent()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* =====================================================
          FOOTER
      ====================================================== */}
      <FooterOne />
    </div>
  );
}

export default DynamicModulePage;
